import { DraftCellType } from './canvas-entity-mapping.model';
import { DisplayValue } from './entity-display.model';

/** The result of reading a dot path: `found: false` when one step of the path does not exist on the data. */
export type PathRead = { found: true; value: unknown } | { found: false };

const NOT_FOUND: PathRead = { found: false };

export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Reads `a.b.c` from an entity's data. A step through anything but a plain object is not found. */
export function readPath(source: unknown, path: string): PathRead {
    return path.split('.').reduce<PathRead>((read, key) => readKey(read, key), { found: true, value: source });
}

function readKey(read: PathRead, key: string): PathRead {
    if (!read.found || !isRecord(read.value) || !(key in read.value)) {
        return NOT_FOUND;
    }

    return { found: true, value: read.value[key] };
}

/**
 * A value as text, or `null` when it is empty: `null`, `undefined`, a blank string, an empty array or an empty
 * object. An array is joined with commas; an object is shown as JSON so that it is visible, never as `[object Object]`.
 */
export function toText(raw: unknown): string | null {
    if (raw === null || raw === undefined) {
        return null;
    }

    if (typeof raw === 'string') {
        return raw.trim() === '' ? null : raw;
    }

    if (Array.isArray(raw)) {
        const items = raw.map(toText).filter((item): item is string => item !== null);

        return items.length === 0 ? null : items.join(', ');
    }

    if (isRecord(raw)) {
        return Object.keys(raw).length === 0 ? null : JSON.stringify(raw);
    }

    return String(raw);
}

/** Draws one raw cell value by its column type; `null` when the value is empty and the cell shows the empty marker. */
export function toDisplayValue(raw: unknown, type: DraftCellType): DisplayValue | null {
    const text = toText(raw);

    if (text === null) {
        return null;
    }

    return type === 'transaction' ? { type, code: text } : { type, text };
}
